import React from 'react';
import { PensionPlanTransfer } from '../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { findArcaPlanById, findArcaPlanByName } from '../../constants/arcaPlans';
import { fmtEur } from '../../utils/formatArcaNumbers';
import toTitleCase from '../../utils/toTitleCase';
import { isNewPlanTransfer, DGSFP_NO_CODE } from '../../types/individualDashboard';

interface TransferInfoCardProps {
	transfer: PensionPlanTransfer;
	translate: (key: string) => string;
	hidePlanInfo?: boolean;
}

const Empty: React.FC<{ label: string }> = ({ label }) => (
	<span className="text-muted-foreground/50 text-xs">{label}</span>
);

const TransferInfoCard: React.FC<TransferInfoCardProps> = ({
	transfer,
	translate,
	hidePlanInfo = false
}) => {
	const planId =
		transfer.selectedArcaPlanId || (transfer.metadata?.selectedPlanId as string | undefined);
	const arcaPlan =
		(planId ? findArcaPlanById(planId) : undefined) ??
		findArcaPlanByName(transfer.metadata?.plan_name ?? '');

	const isTransfer = !isNewPlanTransfer(transfer);
	const empty = translate('individual.process.transferCard.empty') || 'Por confirmar';

	const originName =
		transfer.catalog?.planName ||
		transfer.catalog?.entityName ||
		transfer.originPayload?.entity?.name;
	const originDgs =
		transfer.DGSFP_code && transfer.DGSFP_code !== DGSFP_NO_CODE
			? transfer.DGSFP_code
			: transfer.originPayload?.dgsfpCode;

	const destName = arcaPlan?.name || arcaPlan?.manager;
	const destDgs = arcaPlan?.dgsfpCode;

	const amount = transfer.amount ? parseFloat(transfer.amount) : null;

	if (!isTransfer) {
		if (hidePlanInfo) return null;
		return (
			<div className="surface-card p-5 space-y-4" data-testid="transfer-info-card">
				<div className="space-y-1.5">
					<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
						{translate('individual.process.transferCard.yourPlan') || 'Tu plan'}
					</p>
					<p className="text-sm font-semibold leading-tight">
						{destName ? toTitleCase(destName) : <Empty label={empty} />}
					</p>
					{destDgs && (
						<p className="text-xs text-muted-foreground">
							{translate('individual.process.transferCard.dgs') || 'Código DGS'}:{' '}
							<span className="font-medium text-foreground">{destDgs}</span>
						</p>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className="surface-card p-5 space-y-4" data-testid="transfer-info-card">
			<div className="grid grid-cols-2 gap-4">
				{/* Origen */}
				<div className="space-y-1.5">
					<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
						{translate('individual.dashboard.transfer.origin') || 'Origen'}
					</p>
					<p className="text-sm font-semibold leading-tight min-h-10 flex items-start">
						{originName ? toTitleCase(originName) : <Empty label={empty} />}
					</p>
					{originDgs && (
						<p className="text-xs text-muted-foreground">
							{translate('individual.process.transferCard.dgs') || 'Código DGS'}:{' '}
							<span className="font-medium text-foreground">{originDgs}</span>
						</p>
					)}
				</div>

				{/* Destino */}
				<div className="space-y-1.5">
					<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
						{translate('individual.dashboard.transfer.destination') || 'Destino'}
					</p>
					<p className="text-sm font-semibold leading-tight min-h-10 flex items-start">
						{destName ? toTitleCase(destName) : <Empty label={empty} />}
					</p>
					{destDgs && (
						<p className="text-xs text-muted-foreground">
							{translate('individual.process.transferCard.dgs') || 'Código DGS'}:{' '}
							<span className="font-medium text-foreground">{destDgs}</span>
						</p>
					)}
				</div>
			</div>

			{amount != null && (
				<div className="border-t border-border pt-3">
					<p className="text-xs text-muted-foreground mb-0.5">
						{translate('individual.process.transferCard.amount') || 'Importe a traspasar'}
					</p>
					<p className="text-2xl font-bold text-gradient-brand">{fmtEur(amount)}</p>
				</div>
			)}
		</div>
	);
};

export default TransferInfoCard;
